import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ArrowRightIcon, HeartIcon, MessageSquareIcon } from "lucide-react";

import { formatDateAt } from "simplydo/core/dates";
import { groupCoverImage } from "simplydo/core/images";
import { hasPermission } from "simplydo/core/permissions";
import { RichTextViewer } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { Button, ButtonLink } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

import api from "api";
import { useAppSelector } from "store";

import { UserChip } from "components/lib/Chips";
import { Emoji, EmojiChooser } from "components/lib/Choosers";

import { FeedItemOfType } from "../types";
import {
  FeedItem,
  FeedItemActions,
  FeedItemContent,
  FeedItemDate,
  FeedItemIcon,
  FeedItemMessage,
} from "./FeedItemUI";

const EmojiStack = styled.div`
  display: inline-flex;
  flex-direction: row;
  margin-right: 8px;

  > div {
    margin-right: -2px;
    position: relative;
    z-index: 0;

    &:before {
      position: absolute;
      z-index: -1;
      content: "";
      display: block;
      top: -2px;
      left: -1px;
      width: calc(100% + 2px);
      height: calc(100% + 2px);
      background-color: #fff;
      border-radius: 50%;
    }
  }
`;

type GroupDiscussionFeedItemProps = {
  item: FeedItemOfType<"groupFeedComment">;
};

const GroupDiscussionFeedItem = ({ item }: GroupDiscussionFeedItemProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);

  const discussionPostUrl = `/groups/${item.group}/discussions/${item.discussion}?comment=${item._id}`;

  const canPostReplies = hasPermission(
    user,
    "discussion.postReplies",
    item.discussion,
    item.group,
  );
  const canUseReacts = hasPermission(
    user,
    "discussion.addReacts",
    item.discussion,
    item.group,
  );

  const reactComment = (emoji: string, adding: boolean) => {
    const reactUpdater = adding
      ? api.discussions.commentAddReaction
      : api.discussions.commentRemoveReaction;
    reactUpdater(
      item.discussion,
      item._id,
      emoji,
      (data) => {
        api.setInfiniteQueryData(api.users.feed, [], (prev) => {
          return {
            ...prev,
            pages: prev.pages.map((page) => ({
              ...page,
              feed: page.feed.map((prevItem) =>
                prevItem._id === item._id
                  ? { ...prevItem, reacts: data.reacts }
                  : prevItem,
              ),
            })),
          };
        });
      },
      (err) => toast.error(err.message),
    );
  };

  const uniqueReacts = item.reacts?.filter(
    (react, index, self) =>
      index === self.findIndex((t) => t.emoji === react.emoji),
  );

  const ownReacts = uniqueReacts?.filter(
    (react) => react.ownerUser?._id === user?._id,
  );

  return (
    <FeedItem>
      <FeedItemIcon src={groupCoverImage(item.ownerGroup)} />
      <FeedItemContent>
        <FeedItemMessage>
          <b>
            <UserChip user={item.owner} compact="very" />
          </b>{" "}
          <span>
            posted in{" "}
            <Link
              to={`/groups/${item.group}/discussions/${item.discussion}?comment=${item._id}`}
            >
              {item.discussionTitle}
            </Link>
            .
          </span>
        </FeedItemMessage>
        <RichTextViewer restrictInitialContentHeight value={item.messageHtml} />
        <FeedItemActions>
          <ButtonLink
            variant="ghost"
            size="small"
            to={`/groups/${item.group}/discussions/${item.discussion}?comment=${item._id}&reply=${canPostReplies ? "true" : "false"}`}
          >
            <Icon icon={MessageSquareIcon} />
            <span>{item.replyCount}</span>
          </ButtonLink>
          {canUseReacts ? (
            <Popup
              on="click"
              position="top right"
              offset={[-14, 0]}
              trigger={
                <Button
                  style={{ whiteSpace: "nowrap" }}
                  variant="ghost"
                  size="small"
                >
                  {uniqueReacts && uniqueReacts.length > 0 ? (
                    <EmojiStack>
                      {uniqueReacts.slice(0, 4).map((react) => (
                        <div key={react.emoji}>
                          <Emoji emoji={{ id: react.emoji }} size={10} />
                        </div>
                      ))}
                    </EmojiStack>
                  ) : (
                    <Icon icon={HeartIcon} />
                  )}
                  <span>{item.reacts?.length || 0}</span>
                </Button>
              }
            >
              {
                <EmojiChooser
                  onComplete={(emoji) =>
                    reactComment(
                      emoji,
                      !ownReacts?.some((react) => react.emoji === emoji),
                    )
                  }
                />
              }
            </Popup>
          ) : null}
          <ButtonLink variant="ghost" size="small" to={discussionPostUrl}>
            {t("generic.view")}
            <Icon icon={ArrowRightIcon} />
          </ButtonLink>
        </FeedItemActions>
        <FeedItemDate>{formatDateAt(item.createdAt)}</FeedItemDate>
      </FeedItemContent>
    </FeedItem>
  );
};

export default GroupDiscussionFeedItem;
